import { useEffect, useMemo } from 'react';
import { useMessageContext } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router';
import { useModalContext } from 'src/hooks/useModalContext';
import {
  depositStatusMap,
  getDepositPrevPath,
  removeDepositPrevPath,
  withdrawalStatusMap
} from '../utils';
import {
  clearTransactionType,
  getTransactionType,
  TRANSACTION_TYPE_WITHDRAWAL
} from 'src/utils/transactionTypeFromLS';
import { useRoute } from 'src/router/hooks';

interface DepositReturnProps {
  status: 'success' | 'return' | 'failure' | 'cancel';
}

/** Обрабатывает возвраты с платежек на сайт по заданным адресам
 * deposit-success
 * deposit-failure
 * deposit-return
 * Для каждого из адресов открывает соответствующее сообщение и делает
 * редирект на заранее сохраненный адрес, с которого начался депозит.
 * Если сохраненного адреса нет, делает стандартный редирект на историю транзакций.
 *
 * В обоих случаях учитывает механизм автокредитования. В модалку LATEST_DEPOSIT
 * обязан быть передан проп merchantId, а при редиректе на дефолтный адрес истории
 * транзакций нужно передавать merchantId через navigation state
 */
export function DepositReturn({ status }: DepositReturnProps) {
  const { t } = useTranslation();
  const { merchantId } = useParams<{ merchantId: string }>();
  const { openMessage } = useMessageContext();
  const { pathname } = useLocation();
  const { openModal } = useModalContext();
  const prevPath = getDepositPrevPath();
  const { ROUTE } = useRoute();

  const operationType = getTransactionType();
  /** useMemo нужен, чтобы значение isInitialWithdrawal  фиксировалось при первом рендере.
   т.к. очищение ls происходит до навигации на нужную страницу, то, если убрать useMemo ls очищается и в условия c isInitialWithdrawal не попадает */
  const isInitialWithdrawal = useMemo(() => operationType === TRANSACTION_TYPE_WITHDRAWAL, []);

  useEffect(() => {
    if (openMessage) {
      if (isInitialWithdrawal) {
        openMessage(t(withdrawalStatusMap[status].text), withdrawalStatusMap[status].status);
      } else if (status !== 'cancel') {
        openMessage(t(depositStatusMap[status].text), depositStatusMap[status].status);
      }
    }

    if (!pathname.includes('history') && !isInitialWithdrawal) {
      /** Открываем модальное окно с 10 последними транзакциями */
      openModal('LATEST_DEPOSIT_MODAL', {
        props: { merchantId: status === 'success' ? merchantId : undefined }
      });
    }

    return () => {
      /** После редиректа чистим url в localStorage*/
      removeDepositPrevPath();
    };
  }, []);

  if (isInitialWithdrawal) {
    clearTransactionType();
    return <Navigate to="/account/withdrawal/history" state={{ merchantId }} />;
  }

  clearTransactionType();
  return (
    <Navigate
      to={`${prevPath ? prevPath : `${ROUTE.ACCOUNT_DEPOSIT.get()}/history`}`}
      state={{ merchantId }}
    />
  );
}
